import {config} from '../config'
import {getMissingCachedJoinId, resolveJoinRecords} from './joins'
import {getModel} from './model_metadata'
import {buildJoinPlanMetadata} from './join_plans'
import {getCachedRecord, setCachedRecord} from './records_cache'
import {addDefaultJoinsToRecords} from './add_default_joins_to_records'
import {canSuspendForMissingEntityIndex, suspendForMissingEntityIndex} from './missing_entity_index'

export const buildRecords = (allRecords = {}, key, opts = {}) => {
	const context = createBuildContext(allRecords)
	const records = buildRecordList({context, key, customJoins: opts.joins, records: context.getRecords(key)})
	return config.createAssociation(records)
}

const getSeenKeysScopedCache = (cache, seenKeys) => {
	let scopedCache = cache.get(seenKeys)
	if (scopedCache) return scopedCache
	scopedCache = new Map()
	cache.set(seenKeys, scopedCache)
	return scopedCache
}

const createBuildContext = allRecords => {
	const joinPlanCache = new WeakMap()
	const seenKeysCache = new WeakMap()

	return {
		allRecords,
		getRecords: key => allRecords[key] || [],
		getNextSeenKeys: ({seenKeys, key}) => {
			const cache = getSeenKeysScopedCache(seenKeysCache, seenKeys)
			let nextSeenKeys = cache.get(key)
			if (nextSeenKeys) return nextSeenKeys
			nextSeenKeys = [...seenKeys, key]
			cache.set(key, nextSeenKeys)
			return nextSeenKeys
		},
		getJoinPlan: ({key, customJoins = [], seenKeys = []}) => {
			const cache = getSeenKeysScopedCache(joinPlanCache, seenKeys)
			const cacheKey = `${key}:${customJoins.join(',')}`
			let joinPlan = cache.get(cacheKey)
			if (joinPlan) return joinPlan
			const joinPlanMetadata = buildJoinPlanMetadata({key, customJoins, seenKeys})
			joinPlan = {
				...joinPlanMetadata,
				joinRefs: joinPlanMetadata.defaultJoinCacheKeys.map(key => allRecords[key]),
			}
			cache.set(cacheKey, joinPlan)
			return joinPlan
		},
		resolveJoinRecords: ({record, parentKey, joinDescriptor}) => resolveJoinRecords({record, parentKey, joinDescriptor, allRecords}),
	}
}

const buildRecordList = ({context, key, records, customJoins = [], seenKeys = []}) => {
	const joinPlan = context.getJoinPlan({key, customJoins, seenKeys})
	const nextSeenKeys = context.getNextSeenKeys({seenKeys, key})

	const {defaultJoins, extraJoins, joinRefs} = joinPlan
	const suspendableDefaultJoins = defaultJoins.filter(join => !join.isMultiple && canSuspendForMissingEntityIndex(join.key))
	const entries = records.map(sourceRecord => {
		const cachedModelInstance = getCachedRecord({key, record: sourceRecord, joinRefs})
		if (cachedModelInstance) {
			suspendableDefaultJoins.forEach(joinDescriptor => {
				const missingId = getMissingCachedJoinId(sourceRecord, cachedModelInstance, joinDescriptor)
				if (missingId) suspendForMissingEntityIndex(joinDescriptor.key, missingId, context.allRecords)
			})
			return {sourceRecord, modelInstance: cachedModelInstance, cacheMiss: false}
		}
		return {sourceRecord, modelInstance: buildBaseRecord(sourceRecord, key), cacheMiss: true}
	})
	const cacheMissEntries = entries.filter(entry => entry.cacheMiss)

	addDefaultJoinsToRecords(cacheMissEntries, key, defaultJoins, {context, seenKeys: nextSeenKeys, buildRecordList})
	cacheMissEntries.forEach(({sourceRecord, modelInstance}) => {
		setCachedRecord({
			key,
			record: sourceRecord,
			joinRefs,
			modelInstance,
		})
	})

	const builtRecords = entries.map(({sourceRecord, modelInstance}) => {
		if (!extraJoins.length) return modelInstance
		return addJoinsToRecord(cloneRecord(modelInstance), sourceRecord, key, extraJoins, {context, seenKeys: nextSeenKeys})
	})

	return builtRecords
}

const cloneRecord = record => {
	// Preserve model methods without rerunning constructor/autobind work.
	return Object.assign(Object.create(Object.getPrototypeOf(record)), record)
}

const addJoinsToRecord = (record, sourceRecord, key, joinDescriptors, {context, seenKeys}) => {
	if (!joinDescriptors.length) return record

	joinDescriptors.forEach(joinDescriptor => {
		const joinedRecords = context.resolveJoinRecords({record: sourceRecord, parentKey: key, joinDescriptor})
		if (!joinedRecords) return
		const sourceRecords = joinDescriptor.isMultiple ? joinedRecords : [joinedRecords]
		const builtRecords = buildRecordList({
			context,
			key: joinDescriptor.key,
			records: sourceRecords,
			seenKeys,
		})
		record[joinDescriptor.associationKey] = joinDescriptor.isMultiple ? config.createAssociation(builtRecords) : builtRecords[0]
	})

	return record
}

const buildBaseRecord = (item, key) => {
	const {model} = getModel(key)
	if (!model || item instanceof model) return item
	return new model(item)
}
